import {createResource, getFile, getResource, Phone, Resource, updateResource} from "./common"

export const userTypes = ["unitUser", "orgUser", "bankUser", "partnerUser"]

export type UnitUser = {
	type: "unitUser"
	id: string
	attributes: {
		createdAt: Date
		username: string
		role: string
		isDisabled: boolean
		phone?: Phone
	}
}

export type OrgUser = {
	type: "orgUser"
	id: string
	attributes: {
		createdAt: Date
		username: string
		role: string
		isDisabled: boolean
		phone?: Phone
	}
	relationships: {
		org: {
			data: {
				type: "org"
				id: string
			}
		}
	}
}

export type PartnerUser = {
	type: "partnerUser"
	id: string
	attributes: {
		createdAt: Date
		username: string
		role: string
		isDisabled: boolean
		phone?: Phone
	}
	relationships: {
		partner: {
			data: {
				type: "partner"
				id: string
			}
		}
	}
}

export type BankUser = {
	type: "bankUser"
	id: string
	attributes: {
		createdAt: Date
		username: string
		role: string
		isDisabled: boolean
		phone?: Phone
	}
	relationships: {
		bank: {
			data: {
				type: "bank"
				id: string
			}
		}
	}
}

export type User = UnitUser | OrgUser | BankUser | PartnerUser

export async function findUsers(
	accessToken: string,
	offset: number,
	limit: number,
	orgs?: string[],
	searchQuery?: string
) {
	const orgFilter = orgs && orgs.length > 0 ? orgs : null
	const query = {
		page: {
			limit,
			offset,
		},
		filter: {
			status,
			orgs: orgFilter,
			query: searchQuery != "" ? searchQuery : null,
		},
		include: "org",
	}

	return await getResource<Array<User>>("users", accessToken, query)
}

export async function exportUsers(accessToken: string, searchQuery?: string, orgs?: string[]) {
	const orgFilter = orgs && orgs.length > 0 ? orgs : null
	const query = {
		page: {
			limit: 10000,
			offset: 0,
		},
		filter: {
			orgs: orgFilter,
			query: searchQuery != "" ? searchQuery : null,
		},
		include: "org",
	}

	return await getFile("users", accessToken, query)
}

export async function getUser<User>(accessToken: string, id: string) {
	return await getResource<User>(`users/${id}`, accessToken, {include: "org"})
}

export async function createUnitUser(
	accessToken: string,
	username: string,
	password: string,
	role: string,
	phone: Phone
) {
	const result = await createResource<User>("users", accessToken, {
		type: "unitUser",
		attributes: {
			username,
			password,
			role,
			phone,
		},
	})
	return result.map((v) => v.data)
}

export async function createBankUser(
	accessToken: string,
	username: string,
	password: string,
	bankId: string,
	role: string,
	phone: Phone
) {
	const result = await createResource<User>("users", accessToken, {
		type: "bankUser",
		attributes: {
			username,
			password,
			bankId,
			...{role: mapDisplayRoleToUserRole(role, "bankUser")},
			phone,
		},
	})
	return result.map(({data}) => data)
}

type CreateOrgUserAttributes =
	| {
			username: string
			firstName: string
			lastName: string
			phone: Phone
			roles: string[]
	  }
	| {
			username: string
			firstName: string
			lastName: string
			phone: Phone
			role: string
	  }

export async function createOrgUser(
	accessToken: string,
	orgId: string,
	username: string,
	firstName: string,
	lastName: string,
	role: string,
	phone: Phone
) {
	const mappedRole = mapDisplayRoleToUserRole(role, "orgUser")
	const attributes: CreateOrgUserAttributes = isOrgManagedSolutionsRole(mappedRole)
		? {username, firstName, lastName, phone, roles: [mappedRole]}
		: {username, firstName, lastName, phone, role: mappedRole}

	const result = await createResource<User>("users", accessToken, {
		type: "orgUser",
		attributes,
		relationships: {
			org: {
				data: {
					type: "org",
					id: orgId,
				},
			},
		},
	})
	return result.map((v) => v.data)
}

export async function createPartnerUser(
	accessToken: string,
	username: string,
	firstName: string,
	lastName: string,
	role: string,
	phone: Phone,
	partnerId?: string
) {
	const result = await createResource<User>("users", accessToken, {
		type: "partnerUser",
		attributes: {
			username,
			firstName,
			lastName,
			role,
			partnerId,
			phone,
		},
	})
	return result.map((v) => v.data)
}

export async function createOrgUserByOrgUser(
	accessToken: string,
	username: string,
	firstName: string,
	lastName: string,
	role: string,
	phone: Phone
) {
	const mappedRole = mapDisplayRoleToUserRole(role, "orgUser")
	const attributes: CreateOrgUserAttributes = isOrgManagedSolutionsRole(mappedRole)
		? {username, firstName, lastName, phone, roles: [mappedRole]}
		: {username, firstName, lastName, phone, role: mappedRole}

	const result = await createResource<User>("users", accessToken, {
		type: "orgUser",
		attributes,
	})
	return result.map((v) => v.data)
}

export async function changePassword(
	accessToken: string,
	userId: string,
	oldPassword: string | undefined,
	newPassword: string
) {
	const result = await updateResource<User>(`users/${userId}`, accessToken, {
		type: "user",
		id: userId,
		attributes: {
			oldPassword: oldPassword,
			newPassword: newPassword,
		},
	})
	return result.map((v) => v.data)
}

function mapDisplayRoleToUserRole(role: string, userType: string) {
	if (userType === "bankUser") {
		if (role === "admin") return "bank"
		if (role === "readonly") return "bank-readonly"
		if (role === "operations") return "bank-operations"
	} else if (userType == "orgUser") {
		if (role === "operations") return "org-operations"
	}
	return role
}

const ORG_MANAGED_SOLUTIONS_ROLES = [
	"org_banking_rtl_admin",
	"org_banking_rtl_operational_admin",
	"org_banking_rtl_basic",
] as const

function isOrgManagedSolutionsRole(role: string): role is typeof ORG_MANAGED_SOLUTIONS_ROLES[number] {
	return ORG_MANAGED_SOLUTIONS_ROLES.includes(role as typeof ORG_MANAGED_SOLUTIONS_ROLES[number])
}

export async function updateUser(accessToken: string, userId: string, role: string, userType: string) {
	const userRole = mapDisplayRoleToUserRole(role, userType)
	const result = await updateResource<User>(`users/${userId}`, accessToken, {
		type: userType,
		id: userId,
		attributes: {
			role: userRole,
		},
	})
	return result.map((v) => v.data)
}

function buildPatchAttributes(user: User, updates: {role?: string; phone?: Phone}): Record<string, unknown> {
	const attributes: Record<string, unknown> = {}
	if (updates.role !== undefined) {
		const userRole = mapDisplayRoleToUserRole(updates.role, user.type)
		const useRolesArray = user.type === "orgUser" && isOrgManagedSolutionsRole(userRole)
		if (useRolesArray) {
			attributes.roles = [userRole]
		} else {
			attributes.role = userRole
		}
	}
	if (updates.phone !== undefined) {
		attributes.phone = updates.phone
	}
	return attributes
}

export async function patchUser(accessToken: string, user: User, {role, phone}: {role?: string; phone?: Phone}) {
	const attributes = buildPatchAttributes(user, {role, phone})
	const result = await updateResource<User>(`users/${user.id}`, accessToken, {
		type: user.type,
		id: user.id,
		attributes,
	})
	return result.map((v) => v.data)
}

export async function changeUserStatus(accessToken: string, userId: string, disable: boolean) {
	const result = await createResource<User>(
		disable ? `users/${userId}/disable` : `users/${userId}/enable`,
		accessToken,
		{
			type: "user",
			id: userId,
		}
	)
	return result.map((v) => v.data)
}

export function isUser(resource: Resource): resource is User {
	return userTypes.includes(resource.type)
}

export function mapUsersIdToName(resources: Resource[]) {
	return new Map(
		resources.filter<User>(isUser).map((user, _, __) => {
			return [String(user?.id), user?.attributes?.username]
		})
	)
}
